import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L, { Marker as LeafletMarker, Icon } from "leaflet";
import "leaflet/dist/leaflet.css";
import customMarkerImg from "../assets/Map/koala32.png";
import SearchPicture from "../assets/Map/search-icon.svg";
import type { Photo } from "../types/photo";
import { getGeopointsByAddress } from "../services/geocodeService";
import MarkerClusterGroup from "react-leaflet-cluster";

// Setup custom icon
const customIcon = new Icon({
  iconUrl: customMarkerImg,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

// Props
interface MapComponentProps {
  mode: "upload" | "display";
  lat: number;
  lng: number;
  draggable?: boolean;
  markersFromBackend?: Photo[];
  enableSearch?: boolean;
  onMarkerClick?: (marker: Photo | null) => void;
  userLocation?: { lat: number; lng: number } | null;
  selectedMarker?: Photo | null;
  zoom: number;
}

// Helper: auto change view when center updates
const ChangeView = ({ lat, lng }: { lat: number; lng: number }) => {
  const map = useMap();
  useEffect(() => {
    map.setView([lat, lng], map.getZoom());
  }, [lat, lng, map]);
  return null;
};

// Helper: clear selectedMarker when clicking empty map
const MapClickHandler = ({ onClear }: { onClear: () => void }) => {
  useMapEvents({
    click: () => onClear(),
  });
  return null;
};

// Main component
const MapComponent = ({
  mode,
  lat,
  lng,
  draggable = false,
  markersFromBackend = [],
  enableSearch = true,
  onMarkerClick,
  zoom,
}: MapComponentProps) => {
  const [position, setPosition] = useState<[number, number]>([lat, lng]);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    setPosition([lat, lng]);
  }, [lat, lng]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    try {
      const data = await getGeopointsByAddress(searchQuery);
      if (data.length > 0) {
        const newLat = parseFloat(data[0].lat);
        const newLng = parseFloat(data[0].lon);
        setPosition([newLat, newLng]);
      }
    } catch (error) {
      console.error("Search failed:", error);
    }
  };

  return (
    <div className="h-full w-full relative">
      {/* Search Box (hidden) */}
      {false && enableSearch && mode === "display" && (
        <form
          onSubmit={handleSearch}
          className="hidden md:flex absolute z-40 top-4 left-16 bg-white shadow-md rounded-lg p-2 gap-2 w-[300px]"
        >
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3">
              <img src={SearchPicture} alt="Search" className="w-4 h-4" />
            </div>
            <input
              type="search"
              placeholder="Search location..."
              className="w-full h-full pl-10 pr-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button
            type="submit"
            className="custom-btn"
          >
            Search
          </button>
        </form>
      )}

      {/* Map */}
      <MapContainer
        className="h-full w-full min-w-80 min-h-96 z-0"
        center={position}
        zoom={zoom}
        scrollWheelZoom
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <MapClickHandler onClear={() => onMarkerClick?.(null)} />

        {mode === "upload" && (
          <Marker
            position={position}
            icon={customIcon}
          />
        )}

        <MarkerClusterGroup>
          {mode === "display" && markersFromBackend.map((photo) => (
            <Marker
              key={photo.photoId}
              position={[photo.latitudeDecValue, photo.longitudeDecValue]}
              icon={customIcon}
              eventHandlers={{
                click: (e) => {
                  e.originalEvent.stopPropagation();
                  setPosition([photo.latitudeDecValue, photo.longitudeDecValue]);
                  onMarkerClick?.(photo);
                },
              }}
            />
          ))}
        </MarkerClusterGroup>

        <ChangeView lat={position[0]} lng={position[1]} />
      </MapContainer>
    </div>
  );
};

export default MapComponent;